import { apiClientFetch } from "@packages/api-client/client-config";
import {
	getObjectiveQueryOptions,
	listSubObjectivesByObjectiveQueryOptions,
} from "@packages/api-client/hooks/planningTasksController";
import { ObjectiveStatus, type SubObjective } from "@packages/shared-types";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	Calendar,
	ListTree,
	Pencil,
	Plus,
	Trash2,
} from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	useCreateSubObjective,
	useDeleteSubObjective,
	useUpdateSubObjective,
} from "@/api/planning/objectives";
import { AssigneeSelect } from "@/components/planning/AssigneeSelect";
import { CommentsThread } from "@/components/planning/CommentsThread";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { requireReachableRoute } from "@/lib/offline/routeGuard";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/planning/objectives/$objectiveId/",
)({
	// Objective/sub-objective management is planning desk work — online-only by
	// design. The offline field surface is the task list + task detail.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	loader: async ({ context, params }) => {
		await Promise.all([
			context.queryClient.ensureQueryData(
				getObjectiveQueryOptions(params.orgId, params.objectiveId, {
					client: apiClientFetch,
				}),
			),
			context.queryClient.ensureQueryData(
				listSubObjectivesByObjectiveQueryOptions(
					params.orgId,
					params.objectiveId,
				),
			),
		]);
	},
	component: ObjectiveDetailPage,
});

const STATUS_COLORS: Record<string, string> = {
	DRAFT: "bg-base-300 text-base-content",
	APPROVED: "bg-info text-info-content",
	ACTIVE: "bg-success text-success-content",
	COMPLETED: "bg-primary text-primary-content",
	CANCELLED: "bg-error text-error-content",
};

const PRIORITY_OPTIONS = [
	{ value: 1, key: "critical" },
	{ value: 2, key: "high" },
	{ value: 3, key: "medium" },
	{ value: 4, key: "low" },
] as const;

const emptyForm = {
	name: "",
	startDate: "",
	endDate: "",
	description: "",
	priority: 3,
	assignedToId: undefined as string | undefined,
};

function toDateInput(value?: string) {
	return value ? new Date(value).toISOString().split("T")[0] : "";
}

function ObjectiveDetailPage() {
	const { t } = useTranslation();
	const { formatDateOnly } = useDateFormatter();
	const { orgId, objectiveId } = Route.useParams();

	const { data: objective } = useSuspenseQuery(
		getObjectiveQueryOptions(orgId, objectiveId, { client: apiClientFetch }),
	);
	const { data: subObjectivesData } = useQuery(
		listSubObjectivesByObjectiveQueryOptions(orgId, objectiveId),
	);
	const subObjectives = (subObjectivesData ?? []) as SubObjective[];

	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [isEditOpen, setIsEditOpen] = useState(false);
	const [editing, setEditing] = useState<SubObjective | null>(null);
	const [form, setForm] = useState(emptyForm);

	const createMutation = useCreateSubObjective();
	const updateMutation = useUpdateSubObjective();
	const deleteMutation = useDeleteSubObjective();

	const formId = useId();
	const nameId = `${formId}-name`;
	const startId = `${formId}-start`;
	const endId = `${formId}-end`;
	const priorityId = `${formId}-priority`;
	const descriptionId = `${formId}-description`;
	const assigneeId = `${formId}-assignee`;
	const editFormId = useId();
	const editNameId = `${editFormId}-name`;
	const editStatusId = `${editFormId}-status`;
	const editStartId = `${editFormId}-start`;
	const editEndId = `${editFormId}-end`;
	const editAssigneeId = `${editFormId}-assignee`;

	const handleCreate = async () => {
		if (!form.name) return;
		await createMutation.mutateAsync({
			organizationId: orgId,
			data: {
				objectiveId,
				name: form.name,
				startDate: form.startDate || undefined,
				endDate: form.endDate || undefined,
				description: form.description || undefined,
				priority: form.priority,
				assignedToId: form.assignedToId,
			},
		});
		setIsCreateOpen(false);
		setForm(emptyForm);
	};

	const handleEdit = (subObjective: SubObjective) => {
		setEditing(subObjective);
		setIsEditOpen(true);
	};

	const handleUpdate = async () => {
		if (!editing) return;
		await updateMutation.mutateAsync({
			organizationId: orgId,
			subObjectiveId: editing.id,
			data: {
				name: editing.name,
				status: editing.status,
				startDate: toDateInput(editing.startDate),
				endDate: toDateInput(editing.endDate),
				description: editing.description || undefined,
				priority: editing.priority,
				assignedToId: editing.assignedToId,
			},
		});
		setIsEditOpen(false);
		setEditing(null);
	};

	const handleDelete = async (subObjectiveId: string) => {
		await deleteMutation.mutateAsync({ organizationId: orgId, subObjectiveId });
	};

	return (
		<div className="flex flex-col gap-6 p-4 sm:p-6">
			<Link
				to="/app/org/$orgId/planning/objectives"
				params={{ orgId }}
				search={(prev) => prev}
				className="inline-flex items-center gap-1 text-sm text-base-content/70 hover:text-base-content"
			>
				<ArrowLeft className="h-4 w-4" />
				{t("common.objectives")}
			</Link>

			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="flex flex-col gap-1">
					<h1 className="text-2xl font-semibold">{objective.name}</h1>
					<div className="flex items-center gap-2 text-sm text-base-content/70">
						{objective.year && (
							<span className="flex items-center gap-1">
								<Calendar className="h-4 w-4" />
								{objective.year}
							</span>
						)}
						<Badge className={STATUS_COLORS[objective.status]}>
							{t(`common.${objective.status.toLowerCase()}`)}
						</Badge>
					</div>
					<ObjectivePeople objective={objective} />
				</div>
				<Button onClick={() => setIsCreateOpen(true)}>
					<Plus className="mr-2 h-4 w-4" />
					{t("planning.subObjectives.create")}
				</Button>
			</div>

			<div className="flex flex-col gap-3">
				<h2 className="flex items-center gap-2 text-lg font-medium">
					<ListTree className="h-5 w-5 text-primary" />
					{t("planning.subObjectives.title")}
				</h2>

				{subObjectives.length === 0 ? (
					<div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-base-300 py-12 text-base-content/60">
						<ListTree className="h-10 w-10" />
						<p className="text-sm">{t("planning.subObjectives.empty")}</p>
						<Button variant="outline" onClick={() => setIsCreateOpen(true)}>
							<Plus className="mr-2 h-4 w-4" />
							{t("planning.subObjectives.create")}
						</Button>
					</div>
				) : (
					<div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
						{subObjectives.map((sub) => (
							<Card key={sub.id}>
								<CardHeader className="pb-2">
									<div className="flex items-start justify-between gap-2">
										<CardTitle className="text-base">{sub.name}</CardTitle>
										<Badge className={STATUS_COLORS[sub.status]}>
											{t(`common.${sub.status.toLowerCase()}`)}
										</Badge>
									</div>
								</CardHeader>
								<CardContent className="flex flex-col gap-2">
									{sub.startDate && sub.endDate && (
										<div className="flex items-center gap-1 text-sm text-base-content/70">
											<Calendar className="h-4 w-4" />
											<span>
												{formatDateOnly(new Date(sub.startDate).getTime())} -{" "}
												{formatDateOnly(new Date(sub.endDate).getTime())}
											</span>
										</div>
									)}
									{sub.description && (
										<p className="line-clamp-2 text-sm text-base-content/70">
											{sub.description}
										</p>
									)}
									<div className="mt-2 flex gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => handleEdit(sub)}
										>
											<Pencil className="mr-1 h-4 w-4" />
											{t("common.edit")}
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() => handleDelete(sub.id)}
											disabled={deleteMutation.isPending}
										>
											<Trash2 className="mr-1 h-4 w-4" />
											{t("common.delete")}
										</Button>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				)}
			</div>

			<Card>
				<CardContent className="pt-6">
					<CommentsThread
						organizationId={orgId}
						parentType="OBJECTIVE"
						parentId={objectiveId}
					/>
				</CardContent>
			</Card>

			{/* Create Sub-Objective Dialog */}
			<ResponsiveFormDialog
				open={isCreateOpen}
				onOpenChange={setIsCreateOpen}
				title={t("planning.subObjectives.create")}
				footer={
					<>
						<Button variant="outline" onClick={() => setIsCreateOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={handleCreate}
							disabled={!form.name || createMutation.isPending}
						>
							{createMutation.isPending
								? t("common.creating")
								: t("common.create")}
						</Button>
					</>
				}
			>
				<div className="grid gap-4">
					<div className="grid gap-2">
						<Label htmlFor={nameId}>{t("common.name")} *</Label>
						<Input
							id={nameId}
							value={form.name}
							onChange={(e) => setForm({ ...form, name: e.target.value })}
							placeholder={t("common.name")}
						/>
					</div>
					<div className="grid grid-cols-2 gap-2">
						<div className="grid gap-2">
							<Label htmlFor={startId}>{t("common.startDate")}</Label>
							<Input
								id={startId}
								type="date"
								value={form.startDate}
								onChange={(e) =>
									setForm({ ...form, startDate: e.target.value })
								}
							/>
						</div>
						<div className="grid gap-2">
							<Label htmlFor={endId}>{t("common.endDate")}</Label>
							<Input
								id={endId}
								type="date"
								value={form.endDate}
								onChange={(e) => setForm({ ...form, endDate: e.target.value })}
							/>
						</div>
					</div>
					<div className="grid gap-2">
						<Label htmlFor={priorityId}>{t("common.priority")}</Label>
						<Select
							value={String(form.priority)}
							onValueChange={(value) =>
								setForm({ ...form, priority: Number(value) })
							}
						>
							<SelectTrigger id={priorityId} className="w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{PRIORITY_OPTIONS.map((option) => (
									<SelectItem key={option.value} value={String(option.value)}>
										{t(`planning.tasks.priority.${option.key}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="grid gap-2">
						<Label htmlFor={assigneeId}>
							{t("planning.tasks.detail.assignedTo")}
						</Label>
						<AssigneeSelect
							id={assigneeId}
							organizationId={orgId}
							value={form.assignedToId}
							onChange={(value) => setForm({ ...form, assignedToId: value })}
						/>
					</div>
					<div className="grid gap-2">
						<Label htmlFor={descriptionId}>{t("common.description")}</Label>
						<Textarea
							id={descriptionId}
							value={form.description}
							onChange={(e) =>
								setForm({ ...form, description: e.target.value })
							}
							placeholder={t("common.description")}
							rows={3}
						/>
					</div>
				</div>
			</ResponsiveFormDialog>

			{/* Edit Sub-Objective Dialog */}
			<ResponsiveFormDialog
				open={isEditOpen}
				onOpenChange={(open) => {
					setIsEditOpen(open);
					if (!open) setEditing(null);
				}}
				title={t("planning.subObjectives.editTitle")}
				footer={
					<>
						<Button
							variant="outline"
							onClick={() => {
								setIsEditOpen(false);
								setEditing(null);
							}}
						>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={handleUpdate}
							disabled={!editing?.name || updateMutation.isPending}
						>
							{updateMutation.isPending ? t("common.saving") : t("common.save")}
						</Button>
					</>
				}
			>
				{editing && (
					<div className="grid gap-4">
						<div className="grid gap-2">
							<Label htmlFor={editNameId}>{t("common.name")}</Label>
							<Input
								id={editNameId}
								value={editing.name}
								onChange={(e) =>
									setEditing({ ...editing, name: e.target.value })
								}
							/>
						</div>
						<div className="grid gap-2">
							<Label htmlFor={editStatusId}>{t("common.status")}</Label>
							<Select
								value={editing.status}
								onValueChange={(value: ObjectiveStatus) =>
									setEditing({ ...editing, status: value })
								}
							>
								<SelectTrigger id={editStatusId} className="w-full">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{Object.values(ObjectiveStatus).map((status) => (
										<SelectItem key={status} value={status}>
											{t(`common.${status.toLowerCase()}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="grid grid-cols-2 gap-2">
							<div className="grid gap-2">
								<Label htmlFor={editStartId}>{t("common.startDate")}</Label>
								<Input
									id={editStartId}
									type="date"
									value={toDateInput(editing.startDate)}
									onChange={(e) =>
										setEditing({ ...editing, startDate: e.target.value })
									}
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor={editEndId}>{t("common.endDate")}</Label>
								<Input
									id={editEndId}
									type="date"
									value={toDateInput(editing.endDate)}
									onChange={(e) =>
										setEditing({ ...editing, endDate: e.target.value })
									}
								/>
							</div>
						</div>
						<div className="grid gap-2">
							<Label htmlFor={editAssigneeId}>
								{t("planning.tasks.detail.assignedTo")}
							</Label>
							<AssigneeSelect
								id={editAssigneeId}
								organizationId={orgId}
								value={editing.assignedToId}
								onChange={(value) =>
									setEditing({ ...editing, assignedToId: value })
								}
							/>
						</div>
						<div className="border-t border-base-200 pt-4">
							<CommentsThread
								organizationId={orgId}
								parentType="SUB_OBJECTIVE"
								parentId={editing.id}
							/>
						</div>
					</div>
				)}
			</ResponsiveFormDialog>
		</div>
	);
}

function personName(person?: {
	firstName?: string;
	lastName?: string;
	email: string;
}): string | null {
	if (!person) return null;
	return (
		[person.firstName, person.lastName].filter(Boolean).join(" ") ||
		person.email
	);
}

/** Compact created-by / assigned-to / approved-by row for the objective header. */
function ObjectivePeople({
	objective,
}: {
	objective: {
		createdBy?: { firstName?: string; lastName?: string; email: string };
		assignedTo?: { firstName?: string; lastName?: string; email: string };
		approvedBy?: { firstName?: string; lastName?: string; email: string };
	};
}) {
	const { t } = useTranslation();
	const createdBy = personName(objective.createdBy);
	const assignedTo = personName(objective.assignedTo);
	const approvedBy = personName(objective.approvedBy);

	if (!createdBy && !assignedTo && !approvedBy) return null;

	return (
		<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-base-content/70">
			{assignedTo && (
				<span>
					{t("planning.tasks.detail.assignedTo")}:{" "}
					<span className="text-base-content">{assignedTo}</span>
				</span>
			)}
			{createdBy && (
				<span>
					{t("planning.tasks.detail.createdBy")}:{" "}
					<span className="text-base-content">{createdBy}</span>
				</span>
			)}
			{approvedBy && (
				<span>
					{t("planning.tasks.detail.approvedBy")}:{" "}
					<span className="text-base-content">{approvedBy}</span>
				</span>
			)}
		</div>
	);
}
